import { useState } from "react";
import { toast } from "sonner";
import { submitApplication } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const SKILLS = ["Cooking", "Housekeeping", "Infant Care", "Elderly Care", "Special Needs Care", "Childcare", "Tutoring", "Pet Care"];

export default function Application() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "",
    household_size: "", children_count: "0", elderly_care: false, special_needs: false, pets: false,
    required_skills: [], preferred_start_date: "", budget_range: "", additional_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });
  const toggleSkill = (s) => setForm({ ...form, required_skills: form.required_skills.includes(s) ? form.required_skills.filter(x => x !== s) : [...form.required_skills, s] });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone");
      return;
    }
    setSubmitting(true);
    try {
      await submitApplication(form);
      setSubmitted(true);
      toast.success("Application received — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or WhatsApp us at 9135 1039.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="application-success">
        <CheckCircle2 className="w-16 h-16 text-[#688A4E] mx-auto mb-6"/>
        <div className="overline mb-4 justify-center">Application Received</div>
        <h1 className="font-serif text-5xl text-[#0A1128] mb-6">Thank you, {form.full_name.split(" ")[0]}.</h1>
        <p className="text-lg text-gray-600 leading-relaxed max-w-xl mx-auto mb-8">Your consultant will review your requirements and prepare a Precision Match™ shortlist. Expect to hear from us within 1 business day.</p>
        <button onClick={() => { setSubmitted(false); setForm({ full_name: "", email: "", phone: "", address: "", household_size: "", children_count: "0", elderly_care: false, special_needs: false, pets: false, required_skills: [], preferred_start_date: "", budget_range: "", additional_notes: "" }); }} data-testid="application-new" className="btn-outline">Submit another</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-10 py-20 sm:py-24" data-testid="application-page">
      <div className="overline mb-4">Application Form</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-[#0A1128] leading-tight mb-6">Tell us about <span className="italic text-[#688A4E]">your family</span>.</h1>
      <p className="text-lg text-gray-600 mb-14 max-w-2xl">A few minutes with this form gives our consultants everything they need to hand-pick candidates who truly fit.</p>

      <form onSubmit={onSubmit} className="space-y-14">
        {/* Section 1 */}
        <div>
          <div className="overline mb-6">01 · About You</div>
          <div className="grid sm:grid-cols-2 gap-8">
            <Field label="Full name *" value={form.full_name} onChange={(v) => update("full_name", v)} testId="app-full-name"/>
            <Field label="Email *" value={form.email} onChange={(v) => update("email", v)} type="email" testId="app-email"/>
            <Field label="Phone *" value={form.phone} onChange={(v) => update("phone", v)} testId="app-phone"/>
            <Field label="Address" value={form.address} onChange={(v) => update("address", v)} testId="app-address"/>
          </div>
        </div>

        {/* Section 2 */}
        <div>
          <div className="overline mb-6">02 · Your Household</div>
          <div className="grid sm:grid-cols-2 gap-8">
            <Field label="Household size" value={form.household_size} onChange={(v) => update("household_size", v)} testId="app-household-size" placeholder="e.g. 4 adults"/>
            <Field label="Number of children" value={form.children_count} onChange={(v) => update("children_count", v)} testId="app-children-count"/>
          </div>
          <div className="mt-6 flex flex-wrap gap-6">
            <Check label="Elderly care needed" checked={form.elderly_care} onChange={(v) => update("elderly_care", v)} testId="app-elderly"/>
            <Check label="Special needs care" checked={form.special_needs} onChange={(v) => update("special_needs", v)} testId="app-special"/>
            <Check label="We have pets" checked={form.pets} onChange={(v) => update("pets", v)} testId="app-pets"/>
          </div>
        </div>

        {/* Section 3 */}
        <div>
          <div className="overline mb-6">03 · Required Skills</div>
          <div className="flex flex-wrap gap-3">
            {SKILLS.map((s) => (
              <button
                key={s} type="button"
                data-testid={`app-skill-${s.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => toggleSkill(s)}
                className={`border px-4 py-2 text-sm transition ${form.required_skills.includes(s) ? "border-[#688A4E] bg-[#688A4E] text-white" : "border-gray-300 text-gray-700 hover:border-[#0A1128]"}`}
              >{s}</button>
            ))}
          </div>
        </div>

        {/* Section 4 */}
        <div>
          <div className="overline mb-6">04 · Preferences</div>
          <div className="grid sm:grid-cols-2 gap-8">
            <Field label="Preferred start date" value={form.preferred_start_date} onChange={(v) => update("preferred_start_date", v)} testId="app-start-date" placeholder="e.g. March 2026"/>
            <Field label="Budget range (SGD)" value={form.budget_range} onChange={(v) => update("budget_range", v)} testId="app-budget" placeholder="e.g. 700 – 850"/>
          </div>
          <div className="mt-8">
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-3">Anything else we should know?</label>
            <textarea
              data-testid="app-notes"
              value={form.additional_notes}
              onChange={(e) => update("additional_notes", e.target.value)}
              rows={5}
              className="w-full border border-gray-300 p-4 focus:outline-none focus:border-[#688A4E] bg-transparent text-[#0A1128]"
              placeholder="Family routines, dietary preferences, values that matter to you…"
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-gray-500">Your information is confidential and used only for matching.</div>
          <button type="submit" disabled={submitting} data-testid="app-submit" className="btn-primary disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit Application"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, placeholder = "" }) {
  return (
    <div>
      <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E] text-[#0A1128]"
      />
    </div>
  );
}

function Check({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="w-4 h-4 accent-[#688A4E]"
      />
      <span className="text-sm text-[#0A1128]">{label}</span>
    </label>
  );
}
